
import { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import AppointmentDetailsDialog from '@/components/mentor/AppointmentDetailsDialog';
import DayDetailModal from '@/components/calendar/DayDetailModal';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { getSessionBookings, SessionBookingWithMentee } from '@/utils/sessionBookingsUtils';
import { getMentorAvailability, AvailabilitySlot, getDateSpecificAvailability } from '@/utils/availabilityUtils';

const Calendar = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState<SessionBookingWithMentee[]>([]);
  const [availability, setAvailability] = useState<AvailabilitySlot[]>([]);
  const [dateSpecificAvailability, setDateSpecificAvailability] = useState<Map<string, any[]>>(new Map());
  const [selectedBooking, setSelectedBooking] = useState<SessionBookingWithMentee | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [hoveredDay, setHoveredDay] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, currentDate]);

  const loadData = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      // Load bookings
      const bookingsResult = await getSessionBookings(user.id);
      if (bookingsResult.success && bookingsResult.data) {
        setBookings(bookingsResult.data);
      }

      // Load availability
      const availabilityResult = await getMentorAvailability(user.id);
      if (availabilityResult.success && availabilityResult.data) {
        setAvailability(availabilityResult.data);
      }

      // Load date-specific availability for the current month
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const calendarStart = startOfWeek(monthStart);
      const calendarEnd = endOfWeek(monthEnd);
      const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
      
      const dateSpecificMap = new Map();
      
      // Load date-specific availability for each day in the calendar
      for (const day of calendarDays) {
        const dateString = format(day, 'yyyy-MM-dd');
        const result = await getDateSpecificAvailability(user.id, dateString);
        if (result.success && result.data && result.data.length > 0) {
          dateSpecificMap.set(dateString, result.data);
        }
      }
      
      setDateSpecificAvailability(dateSpecificMap);
    } catch (error) {
      console.error('Error loading calendar data:', error);
      toast({
        title: "Error",
        description: "Failed to load calendar data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const getBookingsForDay = (date: Date) => {
    return bookings.filter(booking => 
      isSameDay(new Date(booking.booked_date), date) && 
      booking.status !== 'cancelled'
    );
  };

  const getAvailabilityForDay = (date: Date) => {
    const dateString = format(date, 'yyyy-MM-dd');
    
    // Check if there's date-specific availability
    if (dateSpecificAvailability.has(dateString)) {
      return dateSpecificAvailability.get(dateString) || [];
    }
    
    // Fall back to weekly recurring availability
    const dayOfWeek = date.getDay();
    return availability.filter(slot => 
      slot.day_of_week === dayOfWeek && 
      slot.is_active
    );
  };

  const handlePreviousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const handleDayClick = (date: Date) => {
    setSelectedDate(date);
  };

  const handleCloseDayModal = () => {
    setSelectedDate(null);
  };

  const handleBookingClick = (booking: SessionBookingWithMentee) => {
    setSelectedBooking(booking);
  };

  const handleCloseDialog = () => {
    setSelectedBooking(null);
  };

  const handleAvailabilityChanged = () => {
    // Reload data when availability changes
    loadData();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Navbar />
        <div className="flex-grow flex justify-center items-center">
          <div className="w-8 h-8 border-4 border-emu-blue border-t-transparent rounded-full animate-spin"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Navbar />
        
        {/* Header Section */}
        <div className="bg-gradient-to-r from-blue-100 to-blue-200 text-gray-800 py-12">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="text-left pt-4">
                <p className="text-blue-600 text-sm uppercase tracking-wide mb-2">MENTOR'S</p>
                <h1 className="text-4xl md:text-5xl font-bold mb-4">My Calendar</h1>
              </div>
            </div>
          </div>
        </div>
        
        <main className="flex-grow py-8">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              {/* Calendar Navigation */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <CalendarIcon className="h-8 w-8 text-emu-blue" />
                  <h2 className="text-2xl font-bold text-gray-900">Schedule Overview</h2>
                </div>
                
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    onClick={handlePreviousMonth}
                    className="flex items-center gap-2"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  
                  <h3 className="text-xl font-semibold text-gray-800 min-w-[200px] text-center">
                    {format(currentDate, 'MMMM yyyy')}
                  </h3>
                  
                  <Button
                    variant="outline"
                    onClick={handleNextMonth}
                    className="flex items-center gap-2"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Calendar Grid */}
              <Card className="p-6">
                <div className="grid grid-cols-7 gap-2 mb-4">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center font-medium text-gray-600 py-2">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-2">
                  {calendarDays.map(day => {
                    const dayBookings = getBookingsForDay(day);
                    const dayAvailability = getAvailabilityForDay(day);
                    const isCurrentMonth = isSameMonth(day, currentDate);
                    const isToday = isSameDay(day, new Date());
                    const dayKey = day.toISOString();
                    const isHovered = hoveredDay === dayKey;
                    const hasAvailability = dayAvailability.length > 0;
                    const hasBookings = dayBookings.length > 0;

                    // Determine background color based on availability
                    let bgColor = 'bg-white';
                    let hoverColor = 'hover:bg-gray-50';
                    
                    if (isCurrentMonth) {
                      if (hasAvailability) {
                        bgColor = 'bg-green-50';
                        hoverColor = 'hover:bg-green-100';
                      } else {
                        bgColor = 'bg-red-50';
                        hoverColor = 'hover:bg-red-100';
                      }
                    } else {
                      bgColor = 'bg-gray-50';
                      hoverColor = 'hover:bg-gray-100';
                    }

                    // Create tooltip content for bookings
                    const tooltipContent = hasBookings ? (
                      <div className="space-y-2">
                        {dayBookings.map(booking => (
                          <div key={booking.id} className="text-xs">
                            <div className="flex items-center gap-1 mb-1">
                              <Clock className="h-3 w-3" />
                              <span className="font-medium">{booking.start_time}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              <span>{booking.mentee.first_name} {booking.mentee.last_name}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null;

                    const dayTile = (
                      <div
                        className={`min-h-[120px] p-2 border rounded-lg transition-all duration-200 cursor-pointer ${bgColor} ${hoverColor} border-gray-200 hover:shadow-sm ${isToday ? 'ring-2 ring-emu-blue' : ''} relative`}
                        onMouseEnter={() => setHoveredDay(dayKey)}
                        onMouseLeave={() => setHoveredDay(null)}
                        onClick={() => handleDayClick(day)}
                      >
                        <div className={`text-sm font-medium mb-2 ${
                          isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                        } ${isToday ? 'text-emu-blue' : ''}`}>
                          {format(day, 'd')}
                        </div>

                        {/* Booking badge at bottom */}
                        {hasBookings && (
                          <div className="absolute bottom-2 left-2 right-2">
                            <Badge className="w-full justify-center bg-blue-100 text-blue-800 hover:bg-blue-200 text-xs font-medium">
                              {dayBookings.length === 1 ? '1 Booking' : `${dayBookings.length} Bookings`}
                            </Badge>
                          </div>
                        )}
                      </div>
                    );

                    return (
                      <div key={dayKey}>
                        {hasBookings ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              {dayTile}
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                              {tooltipContent}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          dayTile
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Updated Legend */}
              <div className="mt-6 flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-50 border border-green-200 rounded-full"></div>
                  <span className="text-gray-600">Available Days</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-50 border border-red-200 rounded-full"></div>
                  <span className="text-gray-600">Unavailable Days</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-100 border border-blue-200 rounded-full"></div>
                  <span className="text-gray-600">Days with Bookings</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 border-2 border-emu-blue rounded-full"></div>
                  <span className="text-gray-600">Today</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 flex justify-center gap-4">
                <Button
                  variant="action"
                  onClick={() => navigate('/my-academy?tab=availability')}
                  className="px-6 py-3"
                >
                  Change Availability
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="px-6 py-3"
                >
                  Return to Dashboard
                </Button>
              </div>
            </div>
          </div>
        </main>

        <Footer />

        {/* Appointment Details Dialog */}
        {selectedBooking && (
          <AppointmentDetailsDialog
            booking={selectedBooking}
            isOpen={!!selectedBooking}
            onClose={handleCloseDialog}
          />
        )}

        {/* Day Detail Modal */}
        <DayDetailModal
          date={selectedDate}
          isOpen={!!selectedDate}
          onClose={handleCloseDayModal}
          bookings={selectedDate ? getBookingsForDay(selectedDate) : []}
          availability={selectedDate ? getAvailabilityForDay(selectedDate) : []}
          onBookingClick={(booking) => {
            handleCloseDayModal();
            handleBookingClick(booking);
          }}
          onAvailabilityChanged={handleAvailabilityChanged}
        />
      </div>
    </TooltipProvider>
  );
};

export default Calendar;
